import React from "react";
import { Modal, Toggle } from "@fluentui/react";
import { DocumentIcon, DeleteIcon, CancelIcon } from "@fluentui/react-icons";
import * as S from "../Styles/ImportCompositionStyles";
import { IDetectedColumns, IImportedRow, processFile, downloadExcelTemplate, downloadCSVTemplate, formatDateForDisplay } from "../Functions/Imports";
import ElementsIAIcon from "../../../../images/ia.svg";
import { ChevronDownIcon, ChevronUpIcon } from "@fluentui/react-icons-mdl2";

interface IImportCompositionProps
{
    isOpen: boolean;
    onClose: () => void;
    onImport: (data: any) => void;
    portfolioModelName?: string;
}

interface IFileData
{
    name: string;
    size: number;
    content: any[];
    headers: string[];
}

const ImportComposition: React.FC<IImportCompositionProps> = ({isOpen, onClose, onImport, portfolioModelName}) =>
{
    const [selectedFile, setSelectedFile] = React.useState<IFileData | null>(null);
    const [fileType, setFileType] = React.useState<'excel' | 'csv'>('csv');
    const [importMode, setImportMode] = React.useState<'replace' | 'append'>('replace');
    const [dateHandling, setDateHandling] = React.useState<'auto' | 'manual'>('auto');
    const [isProcessing, setIsProcessing] = React.useState(false);
    const [errors, setErrors] = React.useState<string[]>([]);
    const [warnings, setWarnings] = React.useState<string[]>([]);
    const [dragOver, setDragOver] = React.useState(false);
    const [importedData, setImportedData] = React.useState<IImportedRow[]>([]);
    const [detectedColumns, setDetectedColumns] = React.useState<IDetectedColumns | null>(null);
    const [expanded, setExpanded] = React.useState<Record<string, boolean>>({});
    
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const errorTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    // Limpiar errores después de 10 segundos
    React.useEffect(() =>
    {
        if(errors.length > 0)
        {
            // Limpiar timeout anterior si existe
            if(errorTimeoutRef.current)
            {
                clearTimeout(errorTimeoutRef.current);
            }
            
            // Establecer nuevo timeout para limpiar errores en 10 segundos
            errorTimeoutRef.current = setTimeout(() =>
            {
                setErrors([]);
                errorTimeoutRef.current = null;
            }, 10000);
        }
        
        // Cleanup function
        return () =>
        {
            if(errorTimeoutRef.current)
            {
                clearTimeout(errorTimeoutRef.current);
                errorTimeoutRef.current = null;
            }
        };
    }, [errors]);

    const handleClose = React.useCallback(() =>
    {
        // Primero ocultamos el modal para evitar efectos visuales extraños
        onClose();
        
        // Luego limpiamos estados después de un pequeño delay
        setTimeout(() =>
        {
            setSelectedFile(null);
            setImportMode('replace');
            setDateHandling('auto');
            setIsProcessing(false);
            setErrors([]);
            setWarnings([]);
            setDragOver(false);
            setImportedData([]);
            setDetectedColumns(null);
            setExpanded({});
            
            // Limpiar timeout de errores
            if(errorTimeoutRef.current)
            {
                clearTimeout(errorTimeoutRef.current);
                errorTimeoutRef.current = null;
            }
        }, 100);
    }, [onClose]);

    const handleFileTypeChange = React.useCallback((e: any, checked?: boolean) =>
    {
        const newFileType = checked ? 'excel' : 'csv';
        setFileType(newFileType);
        
        // Limpiar errores y warnings al cambiar tipo de archivo
        setErrors([]);
        setWarnings([]);
        
        // Limpiar archivo seleccionado si existe
        if(selectedFile)
        {
            setSelectedFile(null);
            setImportedData([]);
            setDetectedColumns(null);
            if(fileInputRef.current)
            {
                fileInputRef.current.value = '';
            }
        }
        
        // Limpiar timeout de errores
        if(errorTimeoutRef.current)
        {
            clearTimeout(errorTimeoutRef.current);
            errorTimeoutRef.current = null;
        }
    }, [selectedFile]);

    const handleDownloadTemplate = React.useCallback((event?: React.MouseEvent) =>
    {
        if(event)
        {
            event.preventDefault();
            event.stopPropagation();
        }
        
        try
        {
            if(fileType === 'excel')
            {
                downloadExcelTemplate();
            }
            else
            {
                downloadCSVTemplate();
            }
        }
        catch(error)
        {
            console.error('Error en descarga:', error);
            setErrors(['Error al descargar la plantilla. Verifica que las descargas estén permitidas en tu navegador.']);
        }
    }, [fileType]);

    const handleFileSelect = React.useCallback(async (file: File) =>
    {
        if(!file)
        {
            return;
        }

        // Validar tipo de archivo según la selección
        const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
        const validExtensions = fileType === 'excel' ? ['.xlsx', '.xls'] : ['.csv'];
        
        if(!validExtensions.includes(fileExtension))
        {
            const expectedFormats = fileType === 'excel' ? 'Excel (.xlsx, .xls)' : 'CSV (.csv)';
            setErrors([`El archivo debe ser de tipo ${expectedFormats} según la selección actual.`]);
            return;
        }

        // Validar tamaño del archivo (máximo 10MB)
        const maxSize = 10 * 1024 * 1024; // 10MB en bytes
        if(file.size > maxSize)
        {
            setErrors(['El archivo es demasiado grande. El tamaño máximo permitido es 10MB.']);
            return;
        }

        setErrors([]);
        setWarnings([]);
        setIsProcessing(true);
        setImportedData([]);
        setDetectedColumns(null);

        try
        {
            // Procesar el archivo usando las funciones de Imports.tsx
            const result = await processFile(file, fileType);
            
            // Guardar los datos procesados
            setImportedData(result.processedData);
            setDetectedColumns(result.detectedColumns);
            setErrors(result.errors);
            setWarnings(result.warnings);
            
            setSelectedFile(
            {
                name: file.name,
                size: file.size,
                content: result.processedData.map(item => `${item.fecha},${item.isin},${item.weight}%`),
                headers: ['Fecha', 'ISIN', 'Peso', 'Disponible']
            });
            
            setIsProcessing(false);
        }
        catch(error)
        {
            setErrors([error instanceof Error ? error.message : 'Error procesando el archivo']);
            setIsProcessing(false);
            setImportedData([]);
            setDetectedColumns(null);
        }
    }, [fileType]);

    const handleDrop = React.useCallback((e: React.DragEvent) =>
    {
        e.preventDefault();
        setDragOver(false);
        
        const files = Array.from(e.dataTransfer.files);
        if(files.length > 0)
        {
            handleFileSelect(files[0]);
        }
    }, [handleFileSelect]);

    const handleDragOver = React.useCallback((e: React.DragEvent) =>
    {
        e.preventDefault();
        setDragOver(true);
    }, []);

    const handleDragLeave = React.useCallback((e: React.DragEvent) =>
    {
        e.preventDefault();
        setDragOver(false);
    }, []);

    const handleFileInputChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) =>
    {
        const files = e.target.files;
        if(files && files.length > 0)
        {
            handleFileSelect(files[0]);
        }
    }, [handleFileSelect]);

    const handleRemoveFile = React.useCallback(() =>
    {
        setSelectedFile(null);
        setErrors([]);
        setWarnings([]);
        setImportedData([]);
        setDetectedColumns(null);
        if(fileInputRef.current)
        {
            fileInputRef.current.value = '';
        }
        
        // Limpiar timeout de errores
        if(errorTimeoutRef.current)
        {
            clearTimeout(errorTimeoutRef.current);
            errorTimeoutRef.current = null;
        }
    }, []);

    const handleImport = React.useCallback(() =>
    {
        if(!selectedFile || importedData.length === 0)
        {
            return;
        }

        const importData = 
        {
            file: selectedFile,
            mode: importMode,
            dateHandling: dateHandling,
            portfolioModelName: portfolioModelName,
            processedData: importedData,
            detectedColumns: detectedColumns,
            totalRecords: importedData.length,
            totalWeight: importedData.reduce((sum, item) => sum + item.weight, 0)
        };

        onImport(importData);
        
        // Primero ocultar el modal
        onClose();
        
        // Luego limpiar estados después de un pequeño delay
        setTimeout(() =>
        {
            setSelectedFile(null);
            setImportMode('replace');
            setDateHandling('auto');
            setIsProcessing(false);
            setErrors([]);
            setWarnings([]);
            setDragOver(false);
            setImportedData([]);
            setDetectedColumns(null);
            setExpanded({});
            
            // Limpiar timeout de errores
            if(errorTimeoutRef.current)
            {
                clearTimeout(errorTimeoutRef.current);
                errorTimeoutRef.current = null;
            }
        }, 100);
    }, [selectedFile, importMode, dateHandling, portfolioModelName, importedData, detectedColumns, onImport, onClose]);

    const formatFileSize = (bytes: number): string =>
    {
        if(bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Determinar si la validación fue exitosa
    const isValidationSuccessful = React.useMemo(() =>
    {
        return selectedFile && 
               importedData.length > 0 && 
               errors.length === 0 && 
               !isProcessing;
    }, [selectedFile, importedData.length, errors.length, isProcessing]);

    // Verificamos si hay fondos no disponibles
    const hasUnavailableFunds = React.useMemo(() =>
    {
        return importedData.some(row => row.available === false);
    }, [importedData]);

    const modalTitle = portfolioModelName 
        ? `Importar Composición - ${portfolioModelName}`
        : "Importar Composición de Cartera";

    const groupedData = React.useMemo(() =>
    {
        return importedData.reduce((acc, row) =>
        {
            const fecha = formatDateForDisplay(row.fecha);
            if(!acc[fecha])
            {
                acc[fecha] = [];
            }
            acc[fecha].push(row);
            return acc;
        }, {} as Record<string, typeof importedData>);

    }, [importedData]);

    const toggleGroup = (fecha: string) =>
    {
        setExpanded((prev) =>
        ({
            ...prev,
            [fecha]: !prev[fecha],
        }));
    };

    return(
        <React.Fragment>
            <S.FluentUIIconFix />
            <Modal isOpen={isOpen} onDismiss={handleClose} isBlocking={true} containerClassName="modal-container" styles={{main: {width: '70vw !important', maxWidth: '1600px', height: 'auto'}}}>
                <S.ModalContainer>
                    <S.ModalTitle>{modalTitle}</S.ModalTitle>
                    <S.ContentContainer>
                        <S.FirstRow>
                            <p>Selecciona un tipo de archivo para la importación de la composición de la cartera. Puedes descargar una plantilla de ejemplo para guiarte.</p>
                        </S.FirstRow>
                        <S.SecondRow>
                            <S.TemplateDownload>
                                <S.TemplateLabel>Plantilla de Ejemplo:</S.TemplateLabel>
                                <S.TemplateButton type="button" onClick={handleDownloadTemplate}>
                                    Descargar {fileType === 'excel' ? 'Excel' : 'CSV'}
                                </S.TemplateButton>
                            </S.TemplateDownload>
                            <S.CompositionPrice>
                                <S.CompositionPriceLabel>Precio de este servicio: 500 Elements IA</S.CompositionPriceLabel>
                                <S.ImageElementsIA alt="IA" src={ElementsIAIcon} />
                            </S.CompositionPrice>
                            <S.FileTypeSelector>
                                <S.FileTypeLabel>Tipo de Archivo:</S.FileTypeLabel>
                                <S.FileTypeOption $isActive={fileType === 'csv'}>CSV</S.FileTypeOption>
                                <Toggle checked={fileType === 'excel'} onChange={handleFileTypeChange}
                                    styles={{root: {margin: '0 8px'}}}
                                />
                                <S.FileTypeOption $isActive={fileType === 'excel'}>Excel</S.FileTypeOption>
                            </S.FileTypeSelector>
                        </S.SecondRow>
                        {!isValidationSuccessful ?
                        (
                            <S.Section>
                                <S.SectionTitle>Seleccionar Archivo {fileType === 'excel' ? 'Excel (.xlsx, .xls)' : 'CSV (.csv)'}</S.SectionTitle>
                                <S.SectionDescription>
                                    {fileType === 'excel' ?
                                    'El archivo debe incluir las siguientes columnas en este orden con cabeceras: Fecha, ISIN y Peso (Ejemplo: 22,3% o 22,3). Todos los importes sumados de la columna de Peso deben sumar 100% por fecha.' : 
                                    'El archivo debe estar separado por comas (,) o punto y coma (;) y debe seguir este orden con cabeceras: Fecha, ISIN y Peso (Ejemplo: 22,3% o 22,3). Todos los importes sumados de la columna de Peso deben sumar 100% por fecha.'}
                                </S.SectionDescription>
                                <S.FileUploadArea className={dragOver ? 'drag-over' : ''} onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <DocumentIcon style={{fontSize: '36px', color: '#605e5c', marginBottom: '12px'}} />
                                    <S.FileUploadText>
                                        Haz clic aquí o arrastra un archivo para subirlo
                                    </S.FileUploadText>
                                    <S.FileUploadSubtext>
                                        Formato esperado: {fileType === 'excel' ? '.xlsx, .xls' : '.csv'} (máximo 10MB)
                                    </S.FileUploadSubtext>
                                </S.FileUploadArea>
                                <S.HiddenInput ref={fileInputRef} type="file" accept={fileType === 'excel' ? '.xlsx,.xls' : '.csv'} onChange={handleFileInputChange} />
                                {selectedFile && !isValidationSuccessful &&
                                (
                                    <S.SelectedFileInfo>
                                        <S.FileInfoText>
                                            <strong>{selectedFile.name}</strong> ({formatFileSize(selectedFile.size)}) - {selectedFile.content.length} registros
                                        </S.FileInfoText>
                                        <S.RemoveFileButton onClick={handleRemoveFile}>
                                            <DeleteIcon /> Quitar
                                        </S.RemoveFileButton>
                                    </S.SelectedFileInfo>
                                )}
                                {errors.length > 0 &&
                                (
                                    <S.ErrorMessage>
                                        <strong>Errores encontrados:</strong>
                                        <ul style={{margin: '8px 0 0 0', paddingLeft: '20px'}}>
                                            {errors.map((error, index) =>
                                            (
                                                <li key={index}>{error}</li>
                                            ))}
                                        </ul>
                                    </S.ErrorMessage>
                                )}
                                {warnings.length > 0 &&
                                (
                                    <S.WarningMessage>
                                        <strong>Advertencias:</strong>
                                        <ul style={{margin: '8px 0 0 0', paddingLeft: '20px'}}>
                                            {warnings.map((warning, index) =>
                                            (
                                                <li key={index}>{warning}</li>
                                            ))}
                                        </ul>
                                    </S.WarningMessage>
                                )}
                            </S.Section>
                        ) :
                        (
                            <S.Section>
                                <S.SectionTitle>Vista Previa de los Datos Importados</S.SectionTitle>
                                <S.FileInfoRow>
                                    <S.FileInfoText>
                                        <S.FileName>{selectedFile?.name}</S.FileName>
                                        <S.FileDetails>
                                            {formatFileSize(selectedFile?.size || 0)} • {importedData.length} registros • {fileType.toUpperCase()}
                                        </S.FileDetails>
                                    </S.FileInfoText>
                                    <S.RemoveFileButton onClick={handleRemoveFile} title="Quitar archivo">
                                        <CancelIcon />
                                    </S.RemoveFileButton>
                                </S.FileInfoRow>
                                <S.DataTableContainer>
                                    <S.TableWrapper>
                                        <S.DataTable>
                                            <S.TableHeader>
                                                <tr>
                                                    <S.TableHeaderCell>Fecha</S.TableHeaderCell>
                                                    <S.TableHeaderCell>ISIN</S.TableHeaderCell>
                                                    <S.TableHeaderCell>Peso (%)</S.TableHeaderCell>
                                                </tr>
                                            </S.TableHeader>
                                            <S.TableBody>
                                                {Object.entries(groupedData).map(([fecha, rows]) =>
                                                {
                                                    const totalWeight = rows.reduce((acc, r) => acc + r.weight, 0);
                                                    const isExpanded = expanded[fecha] ?? false;

                                                    return(
                                                        <React.Fragment key={fecha}>
                                                             {/* Fila resumen */}
                                                             <S.SummaryTableRow onClick={() => toggleGroup(fecha)} style={{cursor: "pointer"}}>
                                                                <S.TableCell>
                                                                    {isExpanded ?
                                                                    (
                                                                        <ChevronUpIcon style={{marginRight: 6}} />
                                                                    ) :
                                                                    (
                                                                        <ChevronDownIcon style={{marginRight: 6}} />
                                                                    )}
                                                                    {fecha}
                                                                </S.TableCell>
                                                                <S.TableCell>{rows.length} ISINs</S.TableCell>
                                                                 <S.TableCell>
                                                                    {totalWeight.toFixed(2).replace(".", ",")}%
                                                                 </S.TableCell>
                                                             </S.SummaryTableRow>
                                                            {/* Filas expandidas */}
                                                            {isExpanded && rows.map((row, idx) =>
                                                            (
                                                                <S.TableRow key={`${fecha}-${idx}`}>
                                                                    <S.TableCell /> {/* celda vacía */}
                                                                    <S.TableCell>{row.isin} {row.available ? '' : '(No disponible)'}</S.TableCell>
                                                                    <S.TableCell>
                                                                        {row.weight.toFixed(2).replace(".", ",")}%
                                                                    </S.TableCell>
                                                                </S.TableRow>
                                                            ))}
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </S.TableBody>
                                        </S.DataTable>
                                    </S.TableWrapper>
                                </S.DataTableContainer>
                                {hasUnavailableFunds &&
                                (
                                    <S.WarningMessage>
                                        <strong>Advertencia:</strong> Uno o más fondos en la composición no están disponibles. 
                                        No permitimos la importación de fondos no disponibles.
                                    </S.WarningMessage>
                                )}
                            </S.Section>
                        )}
                        {isProcessing &&
                        (
                            <S.Section>
                                <S.SectionTitle>Procesando archivo...</S.SectionTitle>
                                <S.SectionDescription>
                                    Por favor espera mientras procesamos tu archivo.
                                </S.SectionDescription>
                            </S.Section>
                        )}
                    </S.ContentContainer>
                    <S.ButtonContainer>
                        <S.CancelButton onClick={handleClose}>
                            Cancelar
                        </S.CancelButton>
                        <S.ImportButton onClick={handleImport} disabled={!selectedFile || isProcessing || errors.length > 0 || hasUnavailableFunds}>
                            {isProcessing ? 'Procesando...' : hasUnavailableFunds ? 'Fondos no disponibles' : 'Importar'}
                        </S.ImportButton>
                    </S.ButtonContainer>
                </S.ModalContainer>
            </Modal>
        </React.Fragment>
    );
};

export default ImportComposition;